/**
 * Opening a matter.
 *
 * Three things are required and none of them is bureaucracy:
 *
 * - a **client**, because a matter without one is not a matter;
 * - a **service**, because it carries the workflow, and a matter with no
 *   workflow has no stages to move through;
 * - a **responsible lawyer**, because a matter nobody is answerable for is how
 *   things get missed.
 *
 * A fourth is enforced by the server: a conflict check must have been run *and
 * decided* for this client. It used to arrive as a 409 that nobody could act on --
 * the message said to go and run a check and no screen in the application could run
 * one, so the form stated a requirement it made unsatisfiable.
 *
 * `ConflictCheckPanel` now appears as soon as a client is chosen, and the check is
 * run and decided in place. Sending somebody away mid-form to find a feature, then
 * back to retype what they had, is how half-finished matters get created.
 */

import {
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type CaseDetail,
} from '@legalcrm/api';
import {
  Card,
  CardBody,
  Form,
  FormActions,
  FormInput,
  FormSelect,
  FormTextarea,
  useZodForm,
} from '@legalcrm/ui';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useAuth } from '@/auth/AuthProvider';

import { ConflictCheckPanel } from './ConflictCheckPanel';
import { PageHeader } from '@/shell/PageHeader';

const schema = z.object({
  client: z.string().min(1, 'Choose a client'),
  service: z.string().min(1, 'Choose a service'),
  responsible_lawyer: z.string().min(1, 'Choose who is answerable for this matter'),
  title: z.string().min(1, 'Give the matter a title'),
  description: z.string(),
});

type Values = z.infer<typeof schema>;

interface Row {
  id: string;
  [key: string]: unknown;
}

function rowsOf<T>(data: { results: T[] } | T[] | undefined): T[] {
  if (!data) return [];
  return Array.isArray(data) ? data : data.results;
}

export function NewCasePage() {
  const navigate = useNavigate();
  const api = useApi();
  const { matterLabel } = useAuth();

  const clients = useApiQuery<{ results: Row[] } | Row[]>(qk.list('clients'), STAFF.clients);
  // Active only. A retired matter type stays readable everywhere else — a matter already
  // under one has to be able to name it — but it must never appear where somebody is
  // choosing, and `open_case` refuses one outright if a stale list offers it anyway.
  const services = useApiQuery<{ results: Row[] } | Row[]>(
    qk.list('services', { is_active: 'true' }),
    STAFF.services,
    { query: { is_active: 'true' } },
  );
  const users = useApiQuery<{ results: Row[] } | Row[]>(qk.list('users'), STAFF.users);

  const form = useZodForm(schema, {
    defaultValues: {
      client: '',
      service: '',
      responsible_lawyer: '',
      title: '',
      description: '',
    },
  });

  // The panel follows whichever client is chosen, and clears itself when that
  // changes -- a check decided for one client says nothing about another.
  const chosenClientId = form.watch('client');
  const chosenClient = rowsOf(clients.data).find((row) => row.id === chosenClientId);
  const chosenClientName = String(
    chosenClient?.display_name ?? chosenClient?.reference ?? '',
  );

  const create = useApiMutation<CaseDetail, Values>({
    mutationFn: (values) =>
      api.post<CaseDetail>(`${STAFF.cases}/`, {
        ...values,
        description: values.description || undefined,
      }),
    invalidates: [qk.all('cases')],
  });

  // Only people who could actually carry a matter. Offering the whole firm and
  // letting the server refuse would be a worse way to learn the same thing.
  const lawyers = rowsOf(users.data).filter((user) =>
    ['lawyer', 'partner', 'org_admin'].includes(String(user.role_key)),
  );

  return (
    <>
      <PageHeader
        title={`New ${matterLabel.toLowerCase()}`}
        description="A client, a service and somebody answerable for it."
      />

      <Card className="max-w-2xl">
        <CardBody>
          <Form
            form={form}
            ariaLabel={`New ${matterLabel.toLowerCase()}`}
            error={create.error}
            onSubmit={async (values) => {
              const matter = await create.mutateAsync(values);
              navigate(`/cases/${matter.id}`);
            }}
          >
            <FormSelect
              form={form}
              name="client"
              label="Client"
              placeholder="Choose a client"
              options={rowsOf(clients.data).map((client) => ({
                value: client.id,
                label: String(client.display_name ?? client.reference ?? client.id),
              }))}
            />

            {chosenClientId && (
              <ConflictCheckPanel
                key={chosenClientId}
                subjectType="client"
                subjectId={chosenClientId}
                subjectName={chosenClientName}
                requirementLabel={`a ${matterLabel.toLowerCase()} can be opened for ${chosenClientName}`}
              />
            )}

            <FormSelect
              form={form}
              name="service"
              label="Service"
              placeholder="Choose a service"
              hint="The service carries the workflow this matter will follow."
              options={rowsOf(services.data).map((service) => ({
                value: service.id,
                label: String(service.name ?? service.key ?? service.id),
              }))}
            />

            <FormSelect
              form={form}
              name="responsible_lawyer"
              label="Responsible lawyer"
              placeholder="Choose a lawyer"
              hint="Answerable for the matter, and the person escalations reach."
              options={lawyers.map((user) => ({
                value: String(user.user ?? user.id),
                label: `${user.full_name || user.email} — ${user.role_name ?? user.role_key}`,
              }))}
            />

            <FormInput
              form={form}
              name="title"
              label="Title"
              hint="What this matter is, in a few words. The client may see it."
            />

            <FormTextarea
              form={form}
              name="description"
              label="Background"
              rows={4}
            />

            <FormActions
              submitLabel={`Open ${matterLabel.toLowerCase()}`}
              busy={create.isPending}
              onCancel={() => navigate('/cases')}
            />
          </Form>
        </CardBody>
      </Card>
    </>
  );
}
